import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { JobsService } from './jobs.service';
import { SignJob } from '../models/signature.model';

/** Mock sign-queue data mirroring the prototype `sigJobs` array. */
const MOCK_SIG_JOBS: SignJob[] = [
  { id: 'TL-2241', name: 'Sarah Martinez & Carlos Martinez', status: 'ready', rejectionReason: null },
  { id: 'TL-2238', name: 'Richard Thompson', status: 'review', rejectionReason: null },
  {
    id: 'TL-2235',
    name: 'Linh Nguyen',
    status: 'signed',
    rejectionReason: null,
    justSigned: true,
    signedAt: 'Today at 2:14 PM',
    signedCoords: { latitude: 47.6062, longitude: -122.3321 },
  },
  { id: 'TL-2230', name: 'Ravi Patel', status: 'signed', rejectionReason: null, justSigned: false },
];

/**
 * Manages the affidavit sign queue, the process server's reusable signature,
 * and job signing state.
 *
 * @remarks
 * `readyJobs` sources from `JobsService.signJobs()` — the live `openJobs`
 * filtered by `jobReadyToSign()` — so the "Ready to sign" section always
 * reflects the current backend state without a separate API endpoint.
 *
 * `reviewJobs` and `signedJobs` continue to use the `_displayJobs` fallback
 * (API sign-queue when populated, mock sigJobs otherwise) for session-state
 * display (e.g. "just signed" green card).
 */
@Injectable({ providedIn: 'root' })
export class SignService {
  private readonly jobsService = inject(JobsService);

  /** Mock sign-queue data for the review/signed sections (Phase 9). */
  readonly sigJobs = signal<SignJob[]>([...MOCK_SIG_JOBS]);

  /**
   * API-sourced sign queue — populated by `SignAdapter.loadSignQueue()`.
   * Used for the review and signed sections only.
   */
  readonly signQueue = signal<SignJob[]>([]);

  /**
   * IDs of jobs signed in the current session.
   * Excludes them from `readyJobs` immediately after "Sign now" so the
   * card disappears without waiting for a `loadJobs()` refresh.
   *
   * @remarks
   * Cleared for a given ID by: `setSignQueue()` (never called in production —
   * its only caller, `SignAdapter.loadSignQueue()`, is unused), `markRejectedByPS()`,
   * and the constructor's reconciliation `effect()` below. Without that last
   * one, an ID added here could never be removed again in practice, silently
   * hiding a job from "Ready to sign" for the rest of the session even after
   * the server reports it needs signing again (2026-07-16 fix — see effect
   * doc comment for the full scenario).
   */
  private readonly justSignedIds = signal(new Set<string>());

  /** Session-level metadata (timestamp + coords) for jobs signed this session. */
  private readonly _signedMeta = signal(
    new Map<string, { signedAt: string; signedCoords?: { latitude: number; longitude: number } | null }>(),
  );

  /**
   * Reconciles `justSignedIds` against server truth on every `signJobs()`
   * change (i.e. after each sync/merge cycle).
   *
   * @remarks
   * Scenario this fixes: PS signs job X → `markSigned()` adds X to
   * `justSignedIds` and optimistically flips `esignStatus` to `'A'` — X
   * correctly drops out of `signJobs()`/`readyJobs()` immediately, no bug
   * yet. If a later sync reports X's `esignStatus` as `'N'` again (e.g. a
   * slow/failed queue-first submission that hadn't actually reached the
   * server, or a genuine server-side revert), X reappears in
   * `JobsService.signJobs()` — but was still permanently excluded from
   * `readyJobs()` here, since nothing else clears `justSignedIds` in
   * production (`setSignQueue()`'s only caller, `SignAdapter.loadSignQueue()`,
   * is never invoked). Reported against a real job whose raw API data
   * satisfied every `signJobs()` condition (`esign_status: 'N'`,
   * `approved_status: 0`, `manner > 0`, `job_status: 1`) yet never
   * reappeared in "Ready to sign" after being signed once.
   *
   * Whenever an ID in `justSignedIds` reappears in `signJobs()` — meaning the
   * server no longer considers it signed — this drops it from
   * `justSignedIds`, so the job becomes visible in "Ready to sign" again
   * instead of staying hidden for the rest of the session.
   */
  constructor() {
    effect(() => {
      const stillNeedsSigning = new Set(this.jobsService.signJobs().map(j => j.id));
      const stale = Array.from(this.justSignedIds()).filter(id => stillNeedsSigning.has(id));
      if (stale.length === 0) return;
      this.justSignedIds.update(set => {
        const next = new Set(set);
        stale.forEach(id => next.delete(id));
        return next;
      });
    });
  }

  /**
   * Source for rejected section — API sign-queue when populated,
   * falls back to mock when offline or before first load.
   */
  private readonly _displayJobs = computed(() =>
    this.signQueue().length > 0 ? this.signQueue() : this.sigJobs(),
  );

  /**
   * Jobs ready to sign — sourced from `JobsService.signJobs()` (live
   * `openJobs` filtered by `jobReadyToSign` predicate).  Jobs already
   * signed this session are excluded immediately via `justSignedIds`.
   */
  readonly readyJobs = computed(() =>
    this.jobsService.signJobs()
      .filter(j => !this.justSignedIds().has(j.id))
      .map(j => ({ id: j.id, name: j.name, status: 'ready' as const, rejectionReason: null })),
  );

  /**
   * Jobs awaiting TL review — sourced from `JobsService.reviewSignJobs()`
   * (live `openJobs` with `esignStatus === 'R'`), same pattern as `readyJobs`.
   * Does NOT use `_displayJobs` mock so real API data is always shown.
   */
  readonly reviewJobs = computed(() =>
    this.jobsService.reviewSignJobs()
      .map((j): SignJob => ({ id: j.id, name: j.name, status: 'review', rejectionReason: null })),
  );

  /**
   * Jobs rejected by TL — must be re-signed.
   * Sourced from `_displayJobs` (API sign-queue when populated, mock otherwise).
   * Shown in the "Ready to sign" section with a rejection banner.
   */
  readonly rejectedJobs = computed(() => this._displayJobs().filter(j => j.status === 'rejected'));

  /**
   * Jobs signed and awaiting TL approval — sourced from `JobsService.signedAwaitingJobs()`.
   * Condition: `esignStatus !== 'N' && approvedStatus === 0 && manner > 0 && jobStatus === 1`.
   */
  readonly signedJobs = computed(() =>
    this.jobsService.signedAwaitingJobs()
      .map((j): SignJob => ({ id: j.id, name: j.name, status: 'signed', rejectionReason: null })),
  );

  /**
   * Subset of `signedJobs` signed in the current session — shown with green highlight card.
   * Overlays `signedAt` and `signedCoords` from session metadata.
   */
  readonly justSignedJobs = computed(() => {
    const meta = this._signedMeta();
    return this.signedJobs()
      .filter(j => this.justSignedIds().has(j.id))
      .map(j => ({ ...j, justSigned: true, ...meta.get(j.id) }));
  });

  /** Subset of `signedJobs` NOT signed in the current session. */
  readonly oldSignedJobs = computed(() =>
    this.signedJobs().filter(j => !this.justSignedIds().has(j.id)),
  );

  /**
   * Count of jobs ready to sign — drives the Sign tab badge.
   * Equals `readyJobs().length` (live count from `JobsService`).
   */
  readonly readyCount = computed(() => this.readyJobs().length);

  /**
   * Sign tab badge count — consistent with `readyJobs`.
   * Replaces the old `signQueue`-only filter so badge and queue are always in sync.
   */
  readonly signBadge = computed(() => this.readyJobs().length);

  /**
   * The process server's reusable signature as a PNG data URL, or `null`
   * when not yet captured.  Pre-filled onto each affidavit canvas.
   */
  readonly savedSignature = signal<string | null>(null);

  /**
   * Signed jobs sorted so just-signed entries (green cards) appear first,
   * followed by previously signed jobs in their original order.
   * The "Signed — awaiting TL approval" section renders this computed
   * instead of two separate loops so ordering is always consistent.
   */
  readonly signedJobsSorted = computed(() => [
    ...this.justSignedJobs(),
    ...this.oldSignedJobs(),
  ]);

  /**
   * Replaces the API-sourced sign queue atomically and clears `justSignedIds`
   * for any job IDs present in the refresh — the green highlight card is only
   * shown until the server confirms the state; once the sign queue returns the
   * job as `'signed'` the card reverts to the standard signed style.
   *
   * @param jobs - Fully mapped `SignJob[]` array.
   */
  setSignQueue(jobs: SignJob[]): void {
    const apiIds = new Set(jobs.map(j => j.id));
    this.justSignedIds.update(set => {
      const next = new Set(set);
      apiIds.forEach(id => next.delete(id));
      return next;
    });
    this.signQueue.set(jobs);
  }

  /**
   * Updates a job in `signQueue` by ID, merging the patch into the matched entry.
   * Called by `markSigned()` to move a rejected job to `'signed'` status in the
   * queue immediately after re-signing, without waiting for an API refresh.
   *
   * @param id    - The job ID to update.
   * @param patch - Partial `SignJob` fields to merge into the matched entry.
   */
  updateSignJob(id: string, patch: Partial<SignJob>): void {
    this.signQueue.update(jobs =>
      jobs.map(j => (j.id === id ? { ...j, ...patch } : j)),
    );
  }

  /**
   * Persists the process server's drawn signature.
   *
   * @param dataUrl - PNG data URL from the `SignatureCanvasDirective`.
   */
  saveSignature(dataUrl: string): void {
    this.savedSignature.set(dataUrl);
  }

  /** Clears the saved signature and returns the queue to canvas-capture mode. */
  clearSignature(): void {
    this.savedSignature.set(null);
  }

  /**
   * Marks a job as signed on confirmed API success, recording the timestamp and optional
   * GPS coordinates. Adds the job to `justSignedIds` so the green highlight card is
   * shown this session, and persists `esignStatus: 'A'` to the job cache.
   *
   * @remarks
   * Use `markQueuedForUpload()` instead when the submission is queued offline —
   * that variant updates the cache without setting the justSigned display flag.
   *
   * @param id           - The job ID to update.
   * @param signedAt     - Human-readable signing timestamp (e.g. `'Today at 2:14 PM'`).
   * @param signedCoords - Structured GPS coordinates or `null`.
   */
  markSigned(
    id: string,
    signedAt: string,
    signedCoords?: { latitude: number; longitude: number } | null,
  ): void {
    this._signedMeta.update(m => { const next = new Map(m); next.set(id, { signedAt, signedCoords }); return next; });
    this.justSignedIds.update(set => { const next = new Set(set); next.add(id); return next; });
    // Move rejected job out of the rejection section in the current sign queue
    // so it reflects the new signed state immediately without an API refresh.
    this.updateSignJob(id, { status: 'signed', rejectionReason: null });
    // Persist as 'A' (signed by process server) so the job leaves the ready-to-sign
    // queue on restart and moves to the signedAwaitingJobs (awaiting TL approval) section.
    this.jobsService.updateJobEsignStatus(id, 'A');
  }

  /**
   * Updates the cached `esignStatus` to `'A'` without setting the justSigned display flag.
   *
   * @remarks
   * Use this for offline/queued submissions where the server has not yet confirmed the
   * signing. The job leaves the "Ready to sign" section and appears in "Signed — awaiting
   * TL approval" (both driven by `esignStatus !== 'N'`), but no green highlight card is
   * shown this session because `justSignedIds` is not updated.
   *
   * @param id - The job ID whose `esignStatus` should be set to `'A'` in the cache.
   */
  markQueuedForUpload(id: string): void {
    this.jobsService.updateJobEsignStatus(id, 'A');
  }

  /**
   * Marks a job as rejected by the process server.
   *
   * @remarks
   * Sets `esignStatus` to `'R'` so the job moves from "Ready to sign" to the
   * "Waiting for TL review" section (`reviewSignJobs` computed). Also removes
   * the job from `justSignedIds` in case it was signed earlier in the session.
   *
   * @param id - The job ID to mark as rejected by the process server.
   */
  markRejectedByPS(id: string): void {
    this.jobsService.updateJobEsignStatus(id, 'R');
    this.justSignedIds.update(set => {
      const next = new Set(set);
      next.delete(id);
      return next;
    });
  }
}
